import * as opt from "lib/opt"
import * as sort from "enums/sort"
import React, { useEffect, useMemo } from "react"
import {
	Table,
	TableBody as MuiTableBody,
	TableCell,
	TableHead as MuiTableHead,
	TableRow,
	TableSortLabel,
	TableContainer,
	Box,
	Skeleton,
	styled,
	Divider,
	alpha,
	Typography,
} from "@mui/material"
import * as util from "lib/util"
import { BaseTableProps, TableBodyProps, TableHeadProps, TableCtx, UseTableCtx } from "lib/table"
import { motion } from "framer-motion"
import * as hooks from "lib/hooks"
import { GetSharedTableStyles, TableContainerStyles, TableTypography, TableHeaderHoverColor, TableSortLabelSx } from "lib/styles/table"

// Animation variants - Enhanced for Material You fluid motion
const tableContainerVariants = {
	hidden: { opacity: 0, y: 10 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
			when: "beforeChildren",
			staggerChildren: 0.04,
		},
	},
}

const headerVariants = {
	hidden: { opacity: 0, y: -8 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			type: "spring" as const,
			stiffness: 350,
			damping: 30,
			duration: 0.4,
		},
	},
}

const rowVariants = {
	hidden: { opacity: 0, y: 8 },
	visible: (i) => ({
		opacity: 1,
		y: 0,
		transition: {
			type: "spring" as const,
			stiffness: 350,
			damping: 30,
			delay: i * 0.025,
			duration: 0.4,
		},
	}),
	tap: {
		scale: 0.998,
		transition: { duration: 0.15 },
	},
}

export const StyledTableHead = styled(MuiTableHead)(({ theme }) => {
	const materialColors = hooks.UseMaterialColors()
	const tt = TableTypography("small")

	return {
		"& th": {
			backgroundColor: "transparent",
			borderBottom: `2px solid ${alpha(materialColors.outline, 0.2)}`,
			fontWeight: tt.headerFontWeight,
			padding: theme.spacing(1.75, 2.5),
			fontSize: tt.headerFontSize,
			letterSpacing: tt.headerLetterSpacing,
			color: materialColors.onSurfaceVariant,
			transition: "all 0.2s ease",
		},
	}
})

export const TableHeaderSection = styled(motion.div)(({ theme }) => {
	const materialColors = hooks.UseMaterialColors()

	return {
		padding: theme.spacing(1, 3),
		backgroundColor: materialColors.isDark
			? alpha(materialColors.surfaceContainer, 0.7)
			: alpha(materialColors.surfaceBright, 0.5),
		backdropFilter: "blur(10px)",
		borderTopLeftRadius: materialColors.shape.corner.extraSmall,
		borderTopRightRadius: materialColors.shape.corner.extraSmall,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		borderBottom: `1px solid ${alpha(materialColors.outline, 0.08)}`,
		transition: "all 0.2s ease",
	}
})

/* eslint-disable-next-line */
const MotionTableRow = motion(TableRow)

export function TableHead<
	T extends Record<string, any>,
	K extends keyof T = keyof T,
	U = unknown,
	S extends string | undefined = string,
>({
	hideActions = false,
	stickyHeader = false,
	headerRowProps = {},
	headerCellProps = {},
	className,
	actionConfig,
	sx,
	...props
}: TableHeadProps<T, K, U, S>) {
	const { order, headCells: headCellsFromCtx } = UseTableCtx<T, K, U, S>()
	const headCells = props.headCells ?? headCellsFromCtx
	const orderBy = order?.Data
	const dir = order?.ToMuiSort()
	const materialColors = hooks.UseMaterialColors()

	return (
		<StyledTableHead
			className={className}
			sx={sx}
			/* eslint-disable-next-line*/
			//@ts-ignore
			component={motion.thead}
			variants={headerVariants}
			initial="hidden"
			animate="visible"
		>
			<TableRow {...headerRowProps}>
				{headCells.Map((cell, index) => {
					// Get the effective sort key - use sortKey if available, otherwise use id
					const effectiveSortKey = cell.sortKey || cell.id

					return (
						<TableCell
							key={(cell.id as any).String?.() || String(cell.id)}
							data-test-selector={`table-header-cell-${index}`}
							align={cell.numeric ? "right" : "left"}
							sortDirection={orderBy === effectiveSortKey ? dir : false}
							sx={{
								// Adds visual separation between numeric cells and adjacent content to make the alignment more apparent
								...(cell.numeric && {
									borderLeft: `1px solid ${alpha(materialColors.outline, 0.2)}`,
								}),
								...(cell.width && {
									width: `${cell.width}%`,
								}),
								...(stickyHeader && {
									position: "sticky",
									top: 0,
									zIndex: 2,
									backgroundColor: materialColors.isDark
										? alpha(materialColors.surfaceContainer, 0.95)
										: alpha(materialColors.surfaceContainer, 0.95),
									backdropFilter: "blur(12px)",
								}),
								...headerCellProps.sx,
							}}
							{...headerCellProps}
						>
							{cell.IsSortable() ? (
								<TableSortLabel
									active={orderBy === effectiveSortKey}
									direction={
										orderBy !== effectiveSortKey || dir === false
											? undefined
											: dir
									}
									onClick={cell.Sort}
									sx={TableSortLabelSx(orderBy === effectiveSortKey)}
								>
									{cell.label}
								</TableSortLabel>
							) : (
								<Box component="span">{cell.label}</Box>
							)}
						</TableCell>
					)
				})}
				{!hideActions && (
					<TableCell
						align={actionConfig?.align ?? "right"}
						sx={{
							whiteSpace: "nowrap",
							overflow: "visible",
							width: actionConfig?.width,
							textAlign: "right",
							...(stickyHeader && {
								position: "sticky",
								top: 0,
								zIndex: 2,
								backgroundColor: materialColors.isDark
									? alpha(materialColors.surfaceContainer, 0.95)
									: alpha(materialColors.surfaceContainer, 0.95),
								backdropFilter: "blur(12px)",
							}),
							...headerCellProps.sx,
						}}
						{...headerCellProps}
					>
						{actionConfig?.label ?? "Actions"}
					</TableCell>
				)}
			</TableRow>
		</StyledTableHead>
	)
}

export function TableBody<
	T extends Record<string, any>,
	K extends keyof T,
	U = unknown,
	S extends string | undefined = string,
>({
	data,
	headCells,
	params,
	hideActions,
	onRowClick,
	rowMapper,
	renderActions,
	getRowId,
	loading,
	noData,
	rowsPerPage = 5,
	selectedRow = opt.None(),
	onRowSelect,
}: TableBodyProps<T, K, U, S>) {
	const prevDataLengthRef = React.useRef(data.length)
	const { theme } = hooks.UseTheme()
	const materialColors = hooks.UseMaterialColors()

	// Update the ref when data changes but only if not loading
	useEffect(() => {
		if (!loading && data.length > 0) {
			prevDataLengthRef.current = data.length
		}
	}, [data, loading])

	// Calculate skeleton row count
	const skeletonRowCount = Math.max(rowsPerPage, prevDataLengthRef.current)

	if (noData) {
		return (
			<MuiTableBody
				component={motion.tbody}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
			>
				<MotionTableRow
					variants={rowVariants}
					initial="hidden"
					animate="visible"
					custom={0}
				>
					<TableCell
						colSpan={headCells.Len() + (hideActions ? 0 : 1)}
						align="center"
						sx={{ py: 10 }}
					>
						<Typography variant="subtitle1">No data found</Typography>
					</TableCell>
				</MotionTableRow>
			</MuiTableBody>
		)
	}

	const isRowSelected = (row: T) => {
		return selectedRow
			.IfSome((selectedItem) => {
				if (getRowId) {
					return getRowId(row) === getRowId(selectedItem)
				}

				// Attempt to find unique ID field
				if ("id" in row && "id" in selectedItem) {
					return row.id === selectedItem.id
				}

				// Fallback - exact object comparison
				return row === selectedItem
			})
			.UnwrapOr(false)
	}

	const handleRowClick = (row: T, event: React.MouseEvent) => {
		// Handle selection
		if (onRowSelect) {
			// If the currently selected row is clicked again, deselect it
			const newSelectedRow = selectedRow.Filter((selected) => selected !== row)
			onRowSelect(newSelectedRow)
		}

		// Call the original onRowClick handler if provided
		if (onRowClick) {
			onRowClick(row, event)
		}
	}

	return (
		<MuiTableBody
			component={motion.tbody}
			initial="hidden"
			animate="visible"
			variants={tableContainerVariants}
		>
			{loading ? (
				<TableLoader
					numRows={skeletonRowCount}
					numCols={headCells.Len() + (hideActions ? 0 : 1)}
				/>
			) : (
				data.Map((row, index) => {
					const isSelected = isRowSelected(row)
					return (
						<MotionTableRow
							key={getRowId?.(row) ?? index}
							data-test-selector={`table-row-${index}`}
							variants={rowVariants}
							initial="hidden"
							animate="visible"
							whileHover={!isSelected ? "hover" : undefined}
							whileTap={onRowClick || onRowSelect ? "tap" : undefined}
							custom={index}
							sx={{
								cursor: onRowClick || onRowSelect ? "pointer" : "default",
								borderBottom: `1px solid ${alpha(materialColors.outline, 0.04)}`,
								transition: "all 0.2s ease",
								...(isSelected && {
									"backgroundColor": alpha(
										materialColors.primaryContainer,
										materialColors.isDark ? 0.5 : 0.3
									),
									"&:hover": {
										backgroundColor: alpha(
											materialColors.primaryContainer,
											materialColors.isDark ? 0.7 : 0.4
										),
									},
									"& .MuiTableCell-root:first-of-type": {
										borderLeft: `3px solid ${materialColors.primary}`,
										paddingLeft:
											+theme
												.spacing(2)
												.substring(0, theme.spacing(2).length - 2) -
											3 +
											"px",
									},
								}),
							}}
							selected={isSelected}
						>
							{headCells.Map((cell) => (
								<TableCell
									key={cell.id.String()}
									align={cell.numeric ? "right" : "left"}
									onClick={(e) => handleRowClick(row, e)}
									sx={{
										...(cell.width && {
											width: `${cell.width}%`,
										}),
										// Adds visual separation between numeric cells and adjacent content to make the alignment more apparent
										...(cell.numeric && {
											borderLeft: `1px solid ${alpha(materialColors.outline, 0.2)}`,
										}),
										padding: theme.spacing(1.75, 2.5),
										fontSize: TableTypography("small").cellFontSize,
										fontWeight: TableTypography("small").cellFontWeight,
										letterSpacing: TableTypography("small").cellLetterSpacing,
										color: materialColors.onSurface,
										borderBottom: "none",
										transition: "all 0.15s ease",
									}}
								>
									{rowMapper?.(row, cell, params) ??
										cell.renderCell?.(row, params) ??
										cell.mapper?.(row[cell.id]) ??
										(row[cell.id] as React.ReactNode)}
								</TableCell>
							))}
							{!hideActions && (
								<TableCell
									align="right"
									sx={{
										padding: theme.spacing(1.75, 2.5),
										fontSize: "0.875rem",
										borderBottom: "none",
										transition: "all 0.15s ease",
									}}
								>
									{renderActions?.(row)}
								</TableCell>
							)}
						</MotionTableRow>
					)
				})
			)}
		</MuiTableBody>
	)
}

export function TableLoader({ numRows, numCols }: { numRows: int; numCols: int }) {
	const { theme } = hooks.UseTheme()
	const materialColors = hooks.UseMaterialColors()

	return util.Range(0, numRows).Map((_, rowIndex) => (
		<MotionTableRow
			key={rowIndex}
			variants={rowVariants}
			initial="hidden"
			animate="visible"
			custom={rowIndex}
		>
			{util.Range(0, numCols).Map((_, colIndex) => (
				<TableCell
					key={colIndex}
					sx={{
						padding: theme.spacing(1.75, 2.5),
						borderBottom: `1px solid ${alpha(materialColors.outline, 0.04)}`,
					}}
				>
					<Skeleton
						animation="wave"
						variant="text"
						sx={{
							backgroundColor: alpha(
								materialColors.isDark
									? materialColors.surfaceContainerHigh
									: materialColors.surfaceContainerHigh,
								0.3
							),
							borderRadius: materialColors.shape.corner.extraSmall,
							height: 20,
						}}
					/>
				</TableCell>
			))}
		</MotionTableRow>
	))
}

export function BaseTable<
	T extends Record<string, any>,
	K extends keyof T,
	U = unknown,
	S extends string | undefined = string,
>({
	data,
	tableContainerProps,
	headCells,
	params,
	config,
	rowsPerPage = 25,
	onRowClick,
	rowMapper,
	renderActions,
	hideActions = false,
	sx,
	page,
	loading,
	noData,
	order,
	getSelectedRow,
	onRowSelect,
	getRowId,
	...tableProps
}: BaseTableProps<T, K, U, S>) {
	/* eslint-disable-next-line */
	const Provider = TableCtx.Provider<T, K, U, S>()
	const materialColors = hooks.UseMaterialColors()

	const selectedRow = tableProps.selectedRow ?? getSelectedRow?.(data) ?? opt.None()

	// Create a default order if none is provided
	const defaultOrder = useMemo(() => {
		// If there are head cells, use the first one as the default sort field
		if (!headCells.IsEmpty()) {
			const firstSortableCell = headCells.Find((cell) => cell.IsSortable())
			return firstSortableCell
				.IfSome((cell) => {
					return sort.Asc(cell.id as any)
				})
				.UnwrapOr(sort.Asc("id"))
		}
		// Fallback to a generic default
		return sort.Asc("id" as any)
	}, [headCells])

	// Use the provided order or fall back to the default
	const effectiveOrder = order || defaultOrder

	const { theme } = hooks.UseTheme()

	const { minWidth = 750, size = "medium", stickyHeader, header } = config || {}

	const sharedStyles = GetSharedTableStyles("small")

	return (
		<Provider value={{ order: effectiveOrder, headCells, params }}>
			<TableContainer sx={{ ...TableContainerStyles(), ...tableContainerProps?.sx }} {...tableContainerProps}>
				{header && (
					<TableHeaderSection
						variants={headerVariants}
						initial="hidden"
						animate="visible"
					>
						<Box
							sx={{
								fontWeight: 500,
								fontSize: "1rem",
								color: materialColors.onSurface,
							}}
						>
							{header.title}
						</Box>
						<Box display="flex" alignItems="center" gap={2}>
							{header.actions?.Map((a, i) => (
								<React.Fragment key={i}>
									{i > 0 && (
										<Divider
											orientation="vertical"
											flexItem
											sx={{
												height: 36,
												opacity: 0.2,
												backgroundColor: materialColors.outline,
											}}
										/>
									)}
									{a}
								</React.Fragment>
							))}
						</Box>
					</TableHeaderSection>
				)}

				<Table
					sx={[sharedStyles.root]}
					size={size}
					stickyHeader={stickyHeader}
					{...tableProps}
				>
					<TableHead hideActions={hideActions} stickyHeader={stickyHeader} />
					<TableBody
						noData={noData}
						data={data}
						headCells={headCells}
						params={params}
						hideActions={hideActions}
						onRowClick={onRowClick}
						rowMapper={rowMapper}
						renderActions={renderActions}
						loading={loading}
						rowsPerPage={rowsPerPage}
						selectedRow={selectedRow}
						onRowSelect={onRowSelect}
						getRowId={getRowId}
					/>
				</Table>
			</TableContainer>
		</Provider>
	)
}
